import { Injectable } from '@angular/core';
import { ProjectMedia } from '../models/project-media.model';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ModelValidationService {
  
  /**
   * Validate that all media models are consistent
   */
  validateMediaModels(mediaList: ProjectMedia[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (!mediaList || mediaList.length === 0) {
      return {
        isValid: true,
        errors: [],
        warnings: ['No media items to validate']
      };
    }

    // Check for duplicate IDs
    const ids = mediaList.map(m => m._id);
    const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      errors.push(`Duplicate media IDs found: ${duplicateIds.join(', ')}`);
    }

    // Check for required fields
    mediaList.forEach((media, index) => {
      if (!media._id) {
        errors.push(`Media at index ${index} has no ID`);
      }
      
      if (!media.type) {
        warnings.push(`Media ${media._id} has no type`);
      }
    });

    // Check for data consistency
    const firstMedia = mediaList[0];
    if (firstMedia) {
      const title = firstMedia.title;
      const quantity = firstMedia.quantity;
      const usageLength = firstMedia.usageLength;
      const usageMedium = firstMedia.usageMedium;

      // Check if all media have the same values (for Apply to All scenarios)
      const inconsistentTitle = mediaList.some(m => m.title !== title);
      const inconsistentQuantity = mediaList.some(m => m.quantity !== quantity);
      const inconsistentUsageLength = mediaList.some(m => m.usageLength !== usageLength);
      const inconsistentUsageMedium = mediaList.some(m => 
        m.usageMedium !== usageMedium
      );

      if (inconsistentTitle) {
        warnings.push('Not all media items have the same title');
      }
      if (inconsistentQuantity) {
        warnings.push('Not all media items have the same quantity');
      }
      if (inconsistentUsageLength) {
        warnings.push('Not all media items have the same usage length');
      }
      if (inconsistentUsageMedium) {
        warnings.push('Not all media items have the same usage medium');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate enum data consistency
   */
  validateEnumData(mediaList: ProjectMedia[], availableTypes: string[], availableMediums: string[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!mediaList || mediaList.length === 0) {
      return {
        isValid: true,
        errors: [],
        warnings: ['No media items to validate']
      };
    }

    // Allow free entry - no validation against predefined enums for media types

    // Allow free entry - no validation against predefined enums for usage mediums

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Comprehensive validation of all models
   */
  validateAllModels(
    mediaList: ProjectMedia[], 
    availableTypes: string[], 
    availableMediums: string[]
  ): ValidationResult {
    const mediaValidation = this.validateMediaModels(mediaList);
    const enumValidation = this.validateEnumData(mediaList, availableTypes, availableMediums);

    return {
      isValid: mediaValidation.isValid && enumValidation.isValid,
      errors: [...mediaValidation.errors, ...enumValidation.errors],
      warnings: [...mediaValidation.warnings, ...enumValidation.warnings]
    };
  }
} 